import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Output,
  Optional,
} from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';

import { CollectionItemDirective } from './collection-item.directive';
import { take } from 'rxjs/operators';
import {
  VALUE_DOWN,
  VALUE_LEFT,
  VALUE_RIGHT,
  VALUE_SPACE,
  VALUE_UP,
} from '@wizeoo/akm-core-lib';

@Directive({
  selector: '[collectionContainer]',
  exportAs: 'wizCollContainer',
})
/** un composant de type collection (list / grid / tree) */
export class CollectionContainerDirective implements AfterViewInit {
  /**
   * LMA: relai du MouseDown sur un item pour éventuel démarrage du drag'n'drop
   * par l'aspect "BaseCollector" du container
   */
  @Output() itemMouseDownEvent: EventEmitter<{}> = new EventEmitter<{}>();

  /**
   * When an item is selected. Either with the space bar on via a click
   */
  @Output() selected: EventEmitter<{}> = new EventEmitter<{}>();

  /**
   * When an item is double clicked
   */
  @Output() doubleClicked: EventEmitter<{}> = new EventEmitter<{}>();

  /**
   * space pour gestion différenciée du selected
   */
  @Output() space: EventEmitter<{}> = new EventEmitter<{}>();

  /**
   * gauche / droite
   */
  @Output() openNode: EventEmitter<{}> = new EventEmitter<{}>();
  @Output() closeNode: EventEmitter<{}> = new EventEmitter<{}>();

  /**
   * dispatch d'un event sur fin de navigation clavier
   * (pour refresh éventuel du visuel, par exemple la marque de focus)
   */
  @Output() keyNavigationEvent: EventEmitter<{}> = new EventEmitter<{}>();

  constructor(
    private elem: ElementRef,
    @Optional() private _viewport: CdkVirtualScrollViewport
  ) {
    // focus à -1
    this.currentFocusedChanged = new BehaviorSubject<number>(-1);
    // itemClic à -1. Sera alimenté par les collectionItem
    this.clicked = new BehaviorSubject<number>(-1);
    // itemClic à -1. Sera alimenté par les collectionItem
    this.dbClicked = new BehaviorSubject<number>(-1);

    // sur clic => modification du focus + dispath de l'event "selected"
    this.clicked.subscribe((clickedIndex) => {
      this.currentFocusIndex = clickedIndex;
      this.selected.emit(this._currentFocusIndex);
    });
    // sur double clic => dispath de l'event "doubleClicked"
    this.dbClicked.subscribe((clickedIndex) => {
      this.doubleClicked.emit(this._currentFocusIndex);
    });
  }

  /**
   * BehaviorSubject  : signale qu'un élément (CollectionItem) est cliqué
   */
  public clicked: BehaviorSubject<number>;

  /**
   * BehaviorSubject  : signale qu'un élément (CollectionItem) est double cliqué
   */
  public dbClicked: BehaviorSubject<number>;

  /** modification de l'index */
  public get currentFocusIndex(): number {
    return this._currentFocusIndex;
  }

  public set currentFocusIndex(value: number) {
    this._currentFocusIndex = value;
    this.currentFocusedChanged.next(this._currentFocusIndex);
  }

  private _currentFocusIndex: number = -1;

  /**
   * BehaviorSubject : signale un changement de focus
   */
  public currentFocusedChanged: BehaviorSubject<number>;

  public get currentFocusObs(): Observable<number> {
    // écouté par les CollectionItem pour prise de focus
    return this.currentFocusedChanged.asObservable();
  }

  /**
   * méthode relai déclenchée par l'item, pour dispatch de l'itemMouseDownEvent
   */
  public itemMouseDown(item: CollectionItemDirective): void {
    // if(WizRoot.verboseMode) {
    //   WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.VERBOSE, e));
    // }
    this.itemMouseDownEvent.emit(item);
  }

  /** pose de l'écouteur de navigation clavier */
  ngAfterViewInit() {
    fromEvent(this.elem.nativeElement, 'keydown').subscribe(
      (event: KeyboardEvent) => {
        // we listen to the keyup to set the ArrownKeyHoldedDown to false
        // the take(1) inside the pipe helps to kill the listener after it first call
        fromEvent(this.elem.nativeElement, 'keyup')
          .pipe(take(1))
          .subscribe((keyupEvent) => {
            this.keyNavigationEvent.emit(this._currentFocusIndex);
          });

        let direction: 1 | -1 = 1; // sens de déplacement
        let isArrow: boolean = false; // déplacement ?
        const maxValue: number = this._viewport
          ? this._viewport.getDataLength() - 1
          : -1;

        switch (event.key) {
          case VALUE_LEFT: // gauche
            this.closeNode.emit(this._currentFocusIndex);
            break;
          case VALUE_RIGHT: // droit
            this.openNode.emit(this._currentFocusIndex);
            break;
          case VALUE_UP: // arrowUp
            direction = -1;
            isArrow = true;
            break;
          case VALUE_DOWN: // arrowDown
            direction = 1;
            isArrow = true;
            break;
          case VALUE_SPACE: //space : select
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            this.selected.emit(this._currentFocusIndex);
            this.space.emit(this._currentFocusIndex);
            break;
        }

        if (isArrow) {
          // pas de déplacement sur space
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();

          if (this._currentFocusIndex < 0) {
            // index -1 sur init
            this.currentFocusIndex = this._viewport.getRenderedRange().start; // focus sur 1er élément des VISIBLES
          } else {
            // +/- 1
            let targetIdx = this._currentFocusIndex + direction;
            if (targetIdx < 0) {
              targetIdx = 0;
            } // tentative < premier
            if (maxValue > -1 && targetIdx > maxValue) {
              targetIdx = maxValue;
            } // tentative > dernier

            this.currentFocusIndex = targetIdx;
          }
          this.ensureIndexIsVisible(this._currentFocusIndex);
        }
      }
    );

    fromEvent(this.elem.nativeElement, 'focus').subscribe(
      (event: FocusEvent) => {
        this.currentFocusIndex = this._viewport.getRenderedRange().start;
        this.keyNavigationEvent.emit(this._currentFocusIndex);
      }
    );

    fromEvent(this.elem.nativeElement, 'blur').subscribe(
      (event: FocusEvent) => {
        this.currentFocusIndex = -1;
        this.keyNavigationEvent.emit(this._currentFocusIndex);
      }
    );
  }

  /** scrollToIndex */
  private ensureIndexIsVisible(pItem: number): void {
    // console.log('ensureIndexIsVisible', this._viewport);
    // pas forcément. Il est peut-être visible
    // TODO : scroll 1/1 seulement si nécessaire
    this._viewport.scrollToIndex(pItem);
  }
}
